export type StylePresetId =
  | "luxury-editorial"
  | "kinetic-saas-dark"
  | "vibrant-maximalist"
  | "tactile-neumorphic"
  | "retrofuture-culture"
  | "brutalist-creative"
  | "sustainable-clarity"
  | "immersive-product"
  | "product-reasoning-studio"
  | "industrial-climate-tech"
  | "playful-wellness-pop"

export type StylePresetColorTokens = {
  background: string
  foreground: string
  card: string
  cardForeground: string
  primary: string
  primaryForeground: string
  primaryGlow: string
  secondary: string
  secondaryForeground: string
  muted: string
  mutedForeground: string
  accent: string
  accentForeground: string
  border: string
  ring: string
}

export type MotionPersonality = {
  name: string
  timing: string
  easing: string
  density: "restrained" | "moderate" | "expressive" | "high-energy"
  use: string[]
  avoid: string[]
}

export type TypographyPersonality = {
  headline: string
  body: string
  rhythm: string
}

export type VisualSystemPersonality = {
  shape: "sharp" | "restrained" | "soft" | "pill" | "mixed"
  elevation: "flat" | "crisp" | "soft" | "glow" | "tactile" | "brutalist"
  density: "compact" | "balanced" | "spacious" | "editorial" | "immersive"
  surface: string
  button: string
  card: string
  radius: {
    base: string
    card: string
    panel: string
    button: string
  }
  shadows: {
    card: string
    elegant: string
    glow: string
  }
  spacing: {
    sectionY: string
    container: string
  }
  fonts?: {
    heading?: string
    body?: string
  }
}

export type StylePreset = {
  id: StylePresetId
  name: string
  summary: string
  figmaTrends: string[]
  bestFor: string[]
  avoidFor: string[]
  colors: StylePresetColorTokens
  motion: MotionPersonality
  typography: TypographyPersonality
  visualSystem: VisualSystemPersonality
  sectionBias: string[]
  avoid: string[]
}

export const stylePresets: StylePreset[] = [
  {
    id: "luxury-editorial",
    name: "Luxury Editorial",
    summary: "Cinematic imagery, oversized type, warm restraint, and slow confident reveals for premium hospitality, fashion, venues, and high-touch services.",
    figmaTrends: ["Bold typography", "Motion design", "Dark mode", "Sustainable clarity"],
    bestFor: ["luxury venues", "hospitality", "fashion", "architecture", "premium services"],
    avoidFor: ["playful youth brands", "developer tools", "price-first commodity services"],
    colors: {
      background: "42 36% 96%",
      foreground: "174 28% 9%",
      card: "42 40% 98%",
      cardForeground: "174 28% 9%",
      primary: "166 76% 24%",
      primaryForeground: "42 40% 98%",
      primaryGlow: "167 55% 42%",
      secondary: "29 58% 88%",
      secondaryForeground: "176 30% 15%",
      muted: "38 26% 88%",
      mutedForeground: "174 11% 38%",
      accent: "18 86% 58%",
      accentForeground: "42 40% 98%",
      border: "37 22% 78%",
      ring: "166 76% 24%",
    },
    motion: {
      name: "slow reveal",
      timing: "650-900ms",
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      density: "restrained",
      use: ["image reveal", "soft parallax", "staggered proof cards", "gentle hover lift"],
      avoid: ["bouncy transitions", "constant looping decoration", "fast marquee"],
    },
    typography: {
      headline: "oversized editorial sans or high-contrast display",
      body: "generous leading with narrow measure",
      rhythm: "large hero type, quiet body copy, cinematic whitespace",
    },
    visualSystem: {
      shape: "restrained",
      elevation: "crisp",
      density: "editorial",
      surface: "thin dividers, image-led bands, restrained cards, and generous whitespace",
      button: "quiet solid buttons or text links; avoid pill-heavy SaaS clusters",
      card: "low-radius editorial panels with thin borders and minimal shadow",
      radius: { base: "0.25rem", card: "0.5rem", panel: "0.75rem", button: "0.25rem" },
      shadows: { card: "0 16px 48px -38px hsl(var(--foreground) / 0.28)", elegant: "0 24px 90px -56px hsl(var(--foreground) / 0.38)", glow: "0 0 0 transparent" },
      spacing: { sectionY: "7rem", container: "80rem" },
    },
    sectionBias: ["StoryStageHero", "FilmstripNarrativeStorySection", "EditorialHero", "ProofAndProcess", "TestimonialWall", "CaseStudyCollectionSection", "FinalCta"],
    avoid: ["busy icon grids", "rainbow gradients", "generic centered SaaS hero"],
  },
  {
    id: "kinetic-saas-dark",
    name: "Kinetic SaaS Dark",
    summary: "Dark product theater, precise motion, glowing data surfaces, and crisp conversion flow for AI, analytics, devtools, and B2B SaaS.",
    figmaTrends: ["Dark mode", "Motion design", "AI experiences", "Bold typography"],
    bestFor: ["AI SaaS", "analytics", "developer tools", "security", "automation platforms"],
    avoidFor: ["wedding venues", "wellness brands", "heritage luxury"],
    colors: {
      background: "222 47% 6%",
      foreground: "210 40% 98%",
      card: "222 44% 9%",
      cardForeground: "210 40% 98%",
      primary: "164 96% 48%",
      primaryForeground: "222 47% 6%",
      primaryGlow: "190 96% 62%",
      secondary: "225 34% 16%",
      secondaryForeground: "210 40% 98%",
      muted: "224 28% 14%",
      mutedForeground: "215 18% 72%",
      accent: "259 91% 67%",
      accentForeground: "210 40% 98%",
      border: "224 26% 20%",
      ring: "164 96% 48%",
    },
    motion: {
      name: "precise kinetic",
      timing: "300-650ms",
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      density: "moderate",
      use: ["product panel reveal", "count-up metrics", "command palette transitions", "hover glow"],
      avoid: ["floaty luxury pacing", "playful bounce", "ornamental collage"],
    },
    typography: {
      headline: "large geometric sans with tight hierarchy",
      body: "compact, scannable, technical but human",
      rhythm: "dashboard-like density balanced by high-contrast hero moments",
    },
    visualSystem: {
      shape: "soft",
      elevation: "glow",
      density: "balanced",
      surface: "dark product panels, luminous borders, precise dashboard surfaces, and selective glow",
      button: "rounded confident CTAs with crisp hover glow",
      card: "dark cards with moderate radius, hairline borders, and controlled neon accents",
      radius: { base: "0.75rem", card: "1.25rem", panel: "1.75rem", button: "999px" },
      shadows: { card: "0 24px 80px -48px hsl(var(--primary) / 0.4)", elegant: "0 30px 110px -60px hsl(var(--primary-glow) / 0.55)", glow: "0 0 56px hsl(var(--primary-glow) / 0.32)" },
      spacing: { sectionY: "6rem", container: "80rem" },
    },
    sectionBias: ["PremiumHeader", "StoryStageHero", "PathwayRibbonStorySection", "StackedProofStorySection", "EditorialHero", "FeatureBento", "CollectionProofStrip", "PremiumFaq"],
    avoid: ["stock lifestyle imagery", "soft beige palettes", "decorative script type"],
  },
  {
    id: "vibrant-maximalist",
    name: "Vibrant Maximalist",
    summary: "Dopamine color, layered visuals, bold type, playful motion, and dense expressive sections for lifestyle, beauty, food, entertainment, and creator brands.",
    figmaTrends: ["Vibrant color palettes", "Maximalism", "Collage", "Motion design"],
    bestFor: ["beauty", "food and beverage", "creator brands", "events", "youth lifestyle"],
    avoidFor: ["legal", "medical", "enterprise security", "formal finance"],
    colors: {
      background: "48 100% 94%",
      foreground: "253 39% 12%",
      card: "0 0% 100%",
      cardForeground: "253 39% 12%",
      primary: "326 92% 54%",
      primaryForeground: "0 0% 100%",
      primaryGlow: "37 100% 58%",
      secondary: "184 88% 77%",
      secondaryForeground: "253 39% 12%",
      muted: "281 100% 92%",
      mutedForeground: "258 20% 38%",
      accent: "37 100% 58%",
      accentForeground: "253 39% 12%",
      border: "326 38% 80%",
      ring: "326 92% 54%",
    },
    motion: {
      name: "playful pop",
      timing: "240-520ms",
      easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      density: "high-energy",
      use: ["staggered cards", "marquee moments", "hover scale", "badge rewards", "kinetic stickers"],
      avoid: ["slow fades only", "all-neutral palettes", "monotone card grids"],
    },
    typography: {
      headline: "oversized, chunky, expressive sans",
      body: "short, punchy, energetic",
      rhythm: "dense visual clusters broken by huge headline moments",
    },
    visualSystem: {
      shape: "mixed",
      elevation: "soft",
      density: "spacious",
      surface: "color-block sections, expressive cards, layered badges, and playful contrast",
      button: "chunky rounded CTAs with energetic hover scale",
      card: "bright cards with large radius, colored borders, and tactile depth",
      radius: { base: "1rem", card: "1.5rem", panel: "2rem", button: "1rem" },
      shadows: { card: "10px 14px 0 hsl(var(--foreground) / 0.12)", elegant: "0 26px 80px -44px hsl(var(--primary) / 0.48)", glow: "0 0 64px hsl(var(--primary-glow) / 0.42)" },
      spacing: { sectionY: "6rem", container: "78rem" },
    },
    sectionBias: ["FeatureBento", "BlogCollectionSection", "TestimonialWall", "FinalCta"],
    avoid: ["too much empty whitespace", "muted single-hue themes", "corporate stock photography"],
  },
  {
    id: "tactile-neumorphic",
    name: "Tactile Neumorphic",
    summary: "Soft raised surfaces, subtle shadows, touchable controls, and calm hierarchy for fintech, wellness, productivity, and premium dashboards.",
    figmaTrends: ["Neumorphism", "Dark mode", "Sustainable clarity", "Motion design"],
    bestFor: ["fintech", "productivity", "wellness apps", "dashboards", "personal finance"],
    avoidFor: ["maximalist campaigns", "brutalist portfolios", "news/media density"],
    colors: {
      background: "210 28% 94%",
      foreground: "215 34% 16%",
      card: "210 30% 97%",
      cardForeground: "215 34% 16%",
      primary: "214 84% 52%",
      primaryForeground: "0 0% 100%",
      primaryGlow: "188 76% 58%",
      secondary: "212 24% 88%",
      secondaryForeground: "215 34% 16%",
      muted: "214 24% 88%",
      mutedForeground: "215 12% 42%",
      accent: "151 62% 45%",
      accentForeground: "0 0% 100%",
      border: "214 18% 78%",
      ring: "214 84% 52%",
    },
    motion: {
      name: "tactile press",
      timing: "220-480ms",
      easing: "ease-out",
      density: "restrained",
      use: ["button press states", "soft elevation", "toggle transitions", "progress movement"],
      avoid: ["heavy parallax", "chaotic collage", "high-frequency animation"],
    },
    typography: {
      headline: "rounded modern sans with comfortable scale",
      body: "clear and calm with generous line-height",
      rhythm: "soft panels, compact controls, and strong affordance clarity",
    },
    visualSystem: {
      shape: "soft",
      elevation: "tactile",
      density: "balanced",
      surface: "raised soft panels, inset controls, calm backgrounds, and touchable affordances",
      button: "rounded tactile controls with press states",
      card: "soft neumorphic cards with broad shadows and gentle contrast",
      radius: { base: "1rem", card: "1.5rem", panel: "2rem", button: "999px" },
      shadows: { card: "12px 18px 38px hsl(var(--foreground) / 0.12), -10px -10px 28px hsl(0 0% 100% / 0.72)", elegant: "18px 22px 70px hsl(var(--foreground) / 0.16)", glow: "0 0 0 transparent" },
      spacing: { sectionY: "5.5rem", container: "76rem" },
    },
    sectionBias: ["PremiumHeader", "FeatureBento", "CollectionProofStrip", "PremiumFaq"],
    avoid: ["low-contrast text", "overly subtle controls", "flat anonymous cards"],
  },
  {
    id: "retrofuture-culture",
    name: "Retrofuture Culture",
    summary: "Neon accents, chrome cues, sci-fi optimism, and expressive type for music, gaming, entertainment, launches, and cultural products.",
    figmaTrends: ["Retrofuturism", "Vibrant color palettes", "Experimental navigation", "Gamified design"],
    bestFor: ["music", "gaming", "entertainment", "creator drops", "tech culture"],
    avoidFor: ["law firms", "healthcare", "traditional professional services"],
    colors: {
      background: "252 55% 7%",
      foreground: "180 100% 94%",
      card: "255 48% 11%",
      cardForeground: "180 100% 94%",
      primary: "180 100% 50%",
      primaryForeground: "252 55% 7%",
      primaryGlow: "302 95% 62%",
      secondary: "264 66% 20%",
      secondaryForeground: "180 100% 94%",
      muted: "253 34% 18%",
      mutedForeground: "184 34% 74%",
      accent: "318 100% 62%",
      accentForeground: "252 55% 7%",
      border: "263 48% 30%",
      ring: "180 100% 50%",
    },
    motion: {
      name: "neon pulse",
      timing: "260-620ms",
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      density: "expressive",
      use: ["glow reveals", "interactive navigation", "progress badges", "hover scanlines"],
      avoid: ["quiet beige editorial", "formal proof walls", "traditional stock imagery"],
    },
    typography: {
      headline: "wide futuristic display or pixel-inspired accents",
      body: "clean sans to keep legibility under expressive visuals",
      rhythm: "neon impact moments balanced with readable content panels",
    },
    visualSystem: {
      shape: "sharp",
      elevation: "glow",
      density: "immersive",
      surface: "dark chrome panels, neon outlines, scanline-friendly bands, and theatrical contrast",
      button: "sharp or capsule neon controls with visible focus glow",
      card: "angular dark panels with glow borders and high-contrast content",
      radius: { base: "0.375rem", card: "0.75rem", panel: "1rem", button: "0.375rem" },
      shadows: { card: "0 0 0 1px hsl(var(--primary) / 0.28), 0 24px 80px -48px hsl(var(--accent) / 0.65)", elegant: "0 0 90px hsl(var(--primary-glow) / 0.38)", glow: "0 0 72px hsl(var(--primary) / 0.48)" },
      spacing: { sectionY: "6.5rem", container: "82rem" },
    },
    sectionBias: ["EditorialHero", "FeatureBento", "CollectionProofStrip", "FinalCta"],
    avoid: ["generic cards", "soft luxury serif mood", "unlabeled experimental navigation"],
  },
  {
    id: "brutalist-creative",
    name: "Brutalist Creative",
    summary: "Raw structure, bold contrast, thick borders, blunt copy, and intentionally unconventional layouts for agencies, portfolios, fashion, and experimental brands.",
    figmaTrends: ["Neo-brutalism", "Bold typography", "Experimental navigation", "Maximalism"],
    bestFor: ["creative agencies", "portfolios", "fashion", "art projects", "experimental launches"],
    avoidFor: ["medical", "senior care", "luxury hospitality", "complex B2B procurement"],
    colors: {
      background: "52 100% 96%",
      foreground: "0 0% 3%",
      card: "0 0% 100%",
      cardForeground: "0 0% 3%",
      primary: "0 0% 3%",
      primaryForeground: "52 100% 96%",
      primaryGlow: "52 100% 50%",
      secondary: "52 100% 50%",
      secondaryForeground: "0 0% 3%",
      muted: "0 0% 90%",
      mutedForeground: "0 0% 28%",
      accent: "9 100% 57%",
      accentForeground: "0 0% 3%",
      border: "0 0% 3%",
      ring: "9 100% 57%",
    },
    motion: {
      name: "hard snap",
      timing: "160-360ms",
      easing: "steps(2, jump-none) or sharp ease-out",
      density: "moderate",
      use: ["snap reveals", "cursor-like transitions", "hard hover offsets", "section jumps"],
      avoid: ["soft glassmorphism", "delicate shadows", "slow luxury fades"],
    },
    typography: {
      headline: "huge blunt sans with tight spacing",
      body: "short direct blocks with strong contrast",
      rhythm: "raw grids, thick dividers, and intentional tension",
    },
    visualSystem: {
      shape: "sharp",
      elevation: "brutalist",
      density: "compact",
      surface: "flat blocks, thick borders, hard offsets, and intentionally raw structure",
      button: "square or low-radius buttons with hard border and offset hover",
      card: "flat cards with thick black borders and no soft shadow",
      radius: { base: "0rem", card: "0rem", panel: "0rem", button: "0rem" },
      shadows: { card: "8px 8px 0 hsl(var(--foreground))", elegant: "12px 12px 0 hsl(var(--foreground))", glow: "0 0 0 transparent" },
      spacing: { sectionY: "5rem", container: "82rem" },
    },
    sectionBias: ["PremiumHeader", "FeatureBento", "BlogCollectionSection", "FinalCta"],
    avoid: ["rounded everything", "subtle pastel-only palettes", "template-like symmetry"],
  },
  {
    id: "sustainable-clarity",
    name: "Sustainable Clarity",
    summary: "Lean, accessible, fast-feeling pages with high contrast, reduced clutter, optimized media, and calm conversion paths.",
    figmaTrends: ["Sustainable Web design", "Accessibility", "Dark mode", "Motion design"],
    bestFor: ["nonprofits", "education", "health", "public service", "B2B trust sites"],
    avoidFor: ["high-energy entertainment", "visual-effects showcases", "trend-heavy campaigns"],
    colors: {
      background: "80 32% 96%",
      foreground: "150 26% 10%",
      card: "0 0% 100%",
      cardForeground: "150 26% 10%",
      primary: "145 58% 29%",
      primaryForeground: "0 0% 100%",
      primaryGlow: "145 44% 48%",
      secondary: "86 34% 86%",
      secondaryForeground: "150 26% 10%",
      muted: "90 24% 88%",
      mutedForeground: "150 10% 34%",
      accent: "35 84% 52%",
      accentForeground: "150 26% 10%",
      border: "92 18% 76%",
      ring: "145 58% 29%",
    },
    motion: {
      name: "accessible calm",
      timing: "350-600ms",
      easing: "ease-out",
      density: "restrained",
      use: ["gentle reveal", "focus-visible clarity", "reduced-motion friendly transitions", "count-up only when meaningful"],
      avoid: ["heavy video backgrounds", "large continuous animation", "low-contrast overlays"],
    },
    typography: {
      headline: "clear humanist sans with accessible scale",
      body: "high contrast, readable measure, descriptive labels",
      rhythm: "clean hierarchy, low cognitive load, strong wayfinding",
    },
    visualSystem: {
      shape: "restrained",
      elevation: "flat",
      density: "balanced",
      surface: "flat accessible sections, clear borders, minimal decoration, and purposeful whitespace",
      button: "clear medium-radius controls with strong focus states",
      card: "flat cards with readable borders and minimal shadow",
      radius: { base: "0.5rem", card: "0.75rem", panel: "1rem", button: "0.5rem" },
      shadows: { card: "0 1px 2px hsl(var(--foreground) / 0.08)", elegant: "0 12px 34px -28px hsl(var(--foreground) / 0.28)", glow: "0 0 0 transparent" },
      spacing: { sectionY: "5.5rem", container: "76rem" },
    },
    sectionBias: ["PremiumHeader", "ComparisonCurtainStorySection", "PathwayRibbonStorySection", "AnatomyStorySection", "EditorialHero", "ProofAndProcess", "PremiumFaq", "PremiumFooter"],
    avoid: ["dark patterns", "visual clutter", "image-heavy pages without purpose"],
  },
  {
    id: "immersive-product",
    name: "Immersive Product",
    summary: "Depth, scroll storytelling, large product media, and optional 3D-ready layouts for hardware, ecommerce, interiors, apps, and launches.",
    figmaTrends: ["3D and immersive elements", "Motion design", "Experimental navigation", "Bold typography"],
    bestFor: ["hardware", "ecommerce", "interiors", "apps", "product launches"],
    avoidFor: ["content-heavy blogs", "small local services without media", "strictly utilitarian admin tools"],
    colors: {
      background: "220 24% 97%",
      foreground: "224 42% 8%",
      card: "0 0% 100%",
      cardForeground: "224 42% 8%",
      primary: "231 82% 56%",
      primaryForeground: "0 0% 100%",
      primaryGlow: "191 91% 55%",
      secondary: "220 32% 88%",
      secondaryForeground: "224 42% 8%",
      muted: "220 24% 90%",
      mutedForeground: "224 12% 38%",
      accent: "14 95% 61%",
      accentForeground: "0 0% 100%",
      border: "220 18% 78%",
      ring: "231 82% 56%",
    },
    motion: {
      name: "scroll depth",
      timing: "450-800ms",
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      density: "expressive",
      use: ["parallax layers", "media drift", "3D-ready hero slots", "scroll-triggered product chapters"],
      avoid: ["static hero-only builds", "flat icon-only sections", "unoptimized giant assets"],
    },
    typography: {
      headline: "large product-launch sans with compact subheads",
      body: "short explanatory copy paired with media evidence",
      rhythm: "immersive hero, product chapters, proof, comparison, CTA",
    },
    visualSystem: {
      shape: "soft",
      elevation: "soft",
      density: "immersive",
      surface: "full-bleed media, product frames, large panels, and cinematic depth",
      button: "rounded product CTAs with strong primary action and secondary inspection links",
      card: "large media-forward cards with moderate radius and product-shadow depth",
      radius: { base: "0.875rem", card: "1.25rem", panel: "2rem", button: "999px" },
      shadows: { card: "0 28px 90px -54px hsl(var(--foreground) / 0.36)", elegant: "0 34px 120px -64px hsl(var(--primary) / 0.44)", glow: "0 0 70px hsl(var(--primary-glow) / 0.3)" },
      spacing: { sectionY: "7rem", container: "86rem" },
    },
    sectionBias: ["StoryStageHero", "AnatomyStorySection", "ComparisonCurtainStorySection", "FilmstripNarrativeStorySection", "StackedProofStorySection", "EditorialHero", "FeatureBento", "CaseStudyCollectionSection", "CollectionProofStrip", "FinalCta"],
    avoid: ["tiny product imagery", "generic abstract gradients", "motion without product meaning"],
  },
  {
    id: "product-reasoning-studio",
    name: "Product Reasoning Studio",
    summary: "Warm product-lab minimalism with editorial serif headlines, lavender intelligence cues, low-radius interface cards, and scroll-driven product reasoning stories for AI design and product workflow tools.",
    figmaTrends: ["AI-native product UI", "Scroll storytelling", "Editorial SaaS", "Contextual workspaces"],
    bestFor: ["AI design agents", "product management tools", "UX research platforms", "collaboration workspaces", "developer/product copilots"],
    avoidFor: ["playful consumer apps", "high-chroma creator brands", "rugged industrial sites", "luxury hospitality"],
    colors: {
      background: "30 18% 98%",
      foreground: "240 13% 9%",
      card: "30 18% 99%",
      cardForeground: "240 13% 9%",
      primary: "246 76% 63%",
      primaryForeground: "0 0% 100%",
      primaryGlow: "252 95% 78%",
      secondary: "36 13% 95%",
      secondaryForeground: "240 13% 9%",
      muted: "36 13% 95%",
      mutedForeground: "240 7% 42%",
      accent: "263 74% 66%",
      accentForeground: "0 0% 100%",
      border: "36 10% 84%",
      ring: "246 76% 63%",
    },
    motion: {
      name: "scroll reasoning",
      timing: "520-900ms",
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      density: "expressive",
      use: ["sticky product canvases", "scale-in chapter handoffs", "context cards drifting into focus", "soft crossfades", "artifact reveals tied to scroll"],
      avoid: ["static dashboard-only pages", "bouncy playful motion", "heavy glass blur", "generic screenshot walls without reasoning"],
    },
    typography: {
      headline: "large light-weight editorial serif for strategic AI/product positioning",
      body: "quiet Manrope UI copy with concise product reasoning language",
      rhythm: "centered editorial hero, proof cloud, context intake, sticky reasoning chapters, artifacts, trust, CTA",
    },
    visualSystem: {
      shape: "restrained",
      elevation: "crisp",
      density: "spacious",
      surface: "warm off-white canvas, pale neutral section bands, lavender intelligence glows, product workspace frames, and low-noise artifact cards",
      button: "compact 8px product buttons with confident purple primary action and quiet neutral secondary actions",
      card: "8-14px interface cards with thin borders, very soft shadows, and content-dense product panels",
      radius: { base: "0.5rem", card: "0.875rem", panel: "1.25rem", button: "0.5rem" },
      shadows: { card: "0 24px 70px -54px hsl(var(--foreground) / 0.32)", elegant: "0 36px 100px -66px hsl(var(--primary) / 0.42)", glow: "0 0 90px hsl(var(--primary-glow) / 0.26)" },
      spacing: { sectionY: "7rem", container: "86rem" },
      fonts: {
        heading: 'Georgia, "Times New Roman", serif',
        body: 'Manrope, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
    },
    sectionBias: ["ProductAwareHero", "StoryStageHero", "PathwayRibbonStorySection", "StackedProofStorySection", "ProductRoleBenefitsSection", "ProductUseCaseGallery", "ProductContextSourcesSection", "ProductReasoningStorySection", "ProductTestimonialsMarquee", "ProductSecurityCtaSection"],
    avoid: ["samey rounded SaaS cards", "dark-only cyber AI styling", "abstract gradients without product surfaces", "one-screen prompt boxes without scroll storytelling"],
  },
  {
    id: "industrial-climate-tech",
    name: "Industrial Climate Tech",
    summary: "Bold French climate-hardware energy: cream technical paper, mineral green, oversized uppercase type, low-radius buttons, product-object staging, and sober sustainability proof.",
    figmaTrends: ["Oversized typography", "Sustainable design", "Product object hero", "Industrial editorial"],
    bestFor: ["climate tech", "energy hardware", "sustainable manufacturing", "building technology", "industrial product launches", "green infrastructure"],
    avoidFor: ["playful consumer apps", "luxury fashion", "AI software dashboards", "soft wellness brands"],
    colors: {
      background: "39 50% 96%",
      foreground: "151 83% 12%",
      card: "40 48% 98%",
      cardForeground: "151 83% 12%",
      primary: "151 83% 12%",
      primaryForeground: "52 55% 94%",
      primaryGlow: "147 38% 58%",
      secondary: "51 54% 91%",
      secondaryForeground: "151 83% 12%",
      muted: "43 40% 90%",
      mutedForeground: "151 18% 32%",
      accent: "165 46% 37%",
      accentForeground: "52 55% 94%",
      border: "42 20% 80%",
      ring: "151 83% 12%",
    },
    motion: {
      name: "industrial reveal",
      timing: "420-760ms",
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      density: "expressive",
      use: ["immersive story heroes", "oversized word reveals", "product object lift", "right-pinned product proof", "left-pinned diagnostic rails", "center-stage product scrollytelling", "stacked proof cards", "before/after curtain reveals", "radial orbit narratives", "exploded product anatomy", "guided pathway ribbons", "editorial filmstrip sequences", "metric block scale", "audience card stagger", "technical proof transitions"],
      avoid: ["floaty SaaS blobs", "neon cyber effects", "bouncy playful motion", "generic eco leaf decoration"],
    },
    typography: {
      headline: "very large black uppercase industrial sans with tight leading",
      body: "direct technical copy with sustainability and performance evidence",
      rhythm: "huge claim, product object, performance proof, audience pathways, manufacturing mission, decisive CTA",
    },
    visualSystem: {
      shape: "restrained",
      elevation: "crisp",
      density: "editorial",
      surface: "warm cream paper, dark mineral green panels, thin technical borders, product shadows, and oversized typographic backdrops",
      button: "compact low-radius industrial buttons with uppercase labels and arrow motion",
      card: "low-radius proof cards with thin borders, high-contrast green moments, and technical spacing",
      radius: { base: "0.375rem", card: "0.625rem", panel: "1.1rem", button: "0.375rem" },
      shadows: { card: "0 18px 54px -44px hsl(var(--foreground) / 0.34)", elegant: "0 32px 100px -70px hsl(var(--foreground) / 0.46)", glow: "0 0 80px hsl(var(--primary-glow) / 0.22)" },
      spacing: { sectionY: "7rem", container: "88rem" },
      fonts: {
        heading: '"Space Grotesk", "Arial Black", Manrope, ui-sans-serif, system-ui, sans-serif',
        body: 'Manrope, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
    },
    sectionBias: ["ClimateImmersiveStoryHero", "ClimateTechHero", "ClimateProductRevealSection", "ClimateAnchoredBenefitsSection", "ClimateLeftPinnedDiagnosticsSection", "ClimateCenterStageStorySection", "ClimateStackedCardsStorySection", "ClimateComparisonCurtainSection", "ClimateOrbitStorySection", "ClimateExplodedAnatomySection", "ClimatePathwayRibbonSection", "ClimateFilmstripStorySection", "ClimatePerformanceGrid", "ClimateMissionAudienceSection", "ClimateRevolutionProofSection", "ClimateManufacturingProofSection", "ClimateProjectSimulatorSection", "ClimateTechCtaSection"],
    avoid: ["pastel eco sameness", "soft rounded SaaS cards", "abstract sustainability claims without metrics", "stock nature imagery replacing the product"],
  },
  {
    id: "playful-wellness-pop",
    name: "Playful Wellness Pop",
    summary: "Cheeky anti-fitness energy, chunky type, off-white paper, hard black outlines, neon app-store accents, and friendly movement/product storytelling for consumer wellness and habit apps.",
    figmaTrends: ["Bold typography", "Vibrant color palettes", "Gamified design", "Motion design"],
    bestFor: ["consumer wellness apps", "habit apps", "productivity breaks", "family health", "playful fitness alternatives"],
    avoidFor: ["formal healthcare", "enterprise procurement", "luxury hospitality", "serious clinical claims"],
    colors: {
      background: "48 38% 98%",
      foreground: "0 0% 4%",
      card: "48 38% 98%",
      cardForeground: "0 0% 4%",
      primary: "78 92% 56%",
      primaryForeground: "0 0% 4%",
      primaryGlow: "63 100% 62%",
      secondary: "184 86% 56%",
      secondaryForeground: "0 0% 4%",
      muted: "51 100% 64%",
      mutedForeground: "0 0% 28%",
      accent: "315 94% 61%",
      accentForeground: "0 0% 4%",
      border: "0 0% 4%",
      ring: "315 94% 61%",
    },
    motion: {
      name: "wiggly snap",
      timing: "180-420ms",
      easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      density: "high-energy",
      use: ["hard hover offsets", "play button pops", "phone tilt", "short carousel beats", "sticker-like reveals"],
      avoid: ["soft corporate fades", "glassmorphism", "quiet monochrome cards", "medical seriousness"],
    },
    typography: {
      headline: "chunky rounded display with comedic confidence",
      body: "clean geometric sans with direct, human microcopy",
      rhythm: "big punchlines, short explanatory copy, playful product proof, and dense colorful cards",
    },
    visualSystem: {
      shape: "pill",
      elevation: "brutalist",
      density: "spacious",
      surface: "off-white paper, thick black dividers, doodle marks, bright app accents, and oversized phone/product mockups",
      button: "pill buttons with thick black borders and hard offset shadows",
      card: "large rounded cards with 3px black borders, high-chroma fills, and hard offset shadows",
      radius: { base: "1.25rem", card: "3rem", panel: "3.5rem", button: "999px" },
      shadows: { card: "6px 6px 0 hsl(var(--foreground))", elegant: "10px 10px 0 hsl(var(--foreground))", glow: "0 0 0 transparent" },
      spacing: { sectionY: "6rem", container: "82rem" },
      fonts: {
        heading: '"Arial Rounded MT Bold", "Trebuchet MS", "Space Grotesk", Manrope, system-ui, sans-serif',
        body: '"Space Grotesk", Manrope, system-ui, sans-serif',
      },
    },
    sectionBias: ["PlayfulAppHero", "StoryStageHero", "FilmstripNarrativeStorySection", "PathwayRibbonStorySection", "StackedProofStorySection", "MovementPreviewStrip", "PlayfulPackGrid", "ReminderLogicSection", "MomentSelectorSection", "PlayfulAwardProofSection", "PlayfulAppCtaSection"],
    avoid: ["generic fitness intensity", "shame-based wellness copy", "thin outline buttons", "low-saturation corporate palettes"],
  },
]

export function getStylePreset(id: StylePresetId) {
  return stylePresets.find((preset) => preset.id === id)
}

export function stylePresetToCssVariables(preset: StylePreset) {
  const variables: Record<string, string> = {
    "--background": preset.colors.background,
    "--foreground": preset.colors.foreground,
    "--card": preset.colors.card,
    "--card-foreground": preset.colors.cardForeground,
    "--popover": preset.colors.card,
    "--popover-foreground": preset.colors.cardForeground,
    "--primary": preset.colors.primary,
    "--primary-foreground": preset.colors.primaryForeground,
    "--primary-glow": preset.colors.primaryGlow,
    "--secondary": preset.colors.secondary,
    "--secondary-foreground": preset.colors.secondaryForeground,
    "--muted": preset.colors.muted,
    "--muted-foreground": preset.colors.mutedForeground,
    "--accent": preset.colors.accent,
    "--accent-foreground": preset.colors.accentForeground,
    "--border": preset.colors.border,
    "--input": preset.colors.border,
    "--ring": preset.colors.ring,
    "--radius": preset.visualSystem.radius.base,
    "--radius-card": preset.visualSystem.radius.card,
    "--radius-panel": preset.visualSystem.radius.panel,
    "--radius-button": preset.visualSystem.radius.button,
    "--shadow-card": preset.visualSystem.shadows.card,
    "--shadow-elegant": preset.visualSystem.shadows.elegant,
    "--shadow-glow": preset.visualSystem.shadows.glow,
    "--section-padding-y": preset.visualSystem.spacing.sectionY,
    "--container-max": preset.visualSystem.spacing.container,
  }

  if (preset.visualSystem.fonts?.heading) variables["--font-heading"] = preset.visualSystem.fonts.heading
  if (preset.visualSystem.fonts?.body) variables["--font-sans"] = preset.visualSystem.fonts.body

  return variables
}
